<template>
  <div>
    <ak-field label="Template Name" for="title" required>
      <ak-input
        ref="title"
        v-model.trim.lazy="localTemplate.title"
        name="title"
        :maxlength="50"
        required
      />
    </ak-field>
    <ak-field label="From" for="from" required>
      <ak-select
        ref="from"
        :model-value="selectedFromValue"
        name="from"
        :options="users"
        track-by="_id"
        required
        :custom-label="selectedFromLabel"
        placeholder="Select email address"
        @select="selectFrom"
      >
        <template #option="{ option }">
          <div>
            <div class="text-small">{{ option.name }}</div>
            <div class="text-overflow">{{ option.contactDetails.email }}</div>
          </div>
        </template>
        <template #singleLabel="{ option }">
          <div>
            <div class="text-small text-overflow">{{ option.name }}</div>
            <div class="text-overflow">{{ option.contactDetails.email }}</div>
          </div>
        </template>
      </ak-select>
    </ak-field>
    <ak-field label="Reply To" for="replyTo">
      <template #helper>
        Set a reply to email address if you would like recipients to reply to a
        different email address
      </template>
      <ak-input
        ref="replyTo"
        v-model.trim.lazy="localTemplate.replyTo"
        name="replyTo"
        type="email"
        placeholder="example@domain.com"
      />
    </ak-field>
    <ak-field label="Email Subject" required>
      <ak-input
        ref="subject"
        v-model.trim.lazy="localTemplate.subject"
        :maxlength="78"
        placeholder="Thank you for your interest..."
        required
      />
    </ak-field>
    <ak-field label="Click Tracking" for="clickTracking">
      <template #helper>
        Track the number of times links have been clicked in the email
      </template>
      <ak-checkbox
        v-model="localTemplate.opts.linkTracking"
        label
        name="clickTracking"
      >
        Enable Click Tracking
      </ak-checkbox>
    </ak-field>

    <ak-folder-form-select
      v-model="localTemplate.folder"
      :active-account="activeAccount"
      :active-agency="activeAgency"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onBeforeMount, defineComponent } from 'vue'
import FolderFormSelect from '../folders/FolderFormSelect.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Checkbox from '@akerolabs/akero-vue/src/components/checkbox/Checkbox.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import store from '@akerolabs/akero-vue/src/store/index'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-templates-details-form></ak-email-templates-details-form>
   */
  name: 'AkEmailTemplatesDetailsForm',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [FolderFormSelect.name]: FolderFormSelect,
    [Field.name]: Field,
    [Input.name]: Input,
    [Select.name]: Select,
    [Checkbox.name]: Checkbox,
    [Button.name]: Button,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The current email template
     */
    template: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Array}
     * @description List of available users
     */
    users: {
      default: () => [],
      type: Array,
    },
  },

  emits: ['update-template'],

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props, { emit }) {
    const localTemplate = ref({})
    const title = ref(null)
    const from = ref(null)
    const email = ref(null)
    const replyTo = ref(null)
    const subject = ref(null)

    const selectedFromValue = computed(() => {
      return props.users.find((o) => o.id === localTemplate.value.from)
    })

    /**
     * @description Runs the form validation
     */
    const validate = (force) => {
      return (
        from.value.validate(force) &&
        title.value.validate(force) &&
        subject.value.validate(force)
      )
    }

    const selectedFromLabel = ({ name, contactDetails }) => {
      return `
        <div>
          <div class="text-small">${name}</div>
          ${contactDetails.email}
        </div>
        `
    }

    const selectFrom = (val) => {
      const { id, contactDetails, accounts } = val
      const accountId = store.getters['session/account']
      const fn = (i) => i.account === accountId
      const acc = accounts.find(fn)

      localTemplate.value.from = id

      if (!localTemplate.value.replyTo) {
        replyTo.value.computedValue = contactDetails.email
      }

      if (acc && acc.settings) {
        const [sig] = acc.settings.signature
        if (sig) {
          localTemplate.value.opts.signature.id = sig._id
        }
      }
    }

    watch(
      localTemplate,
      (val) => {
        emit('update-template', val)
      },
      { deep: true },
    )

    onBeforeMount(() => {
      localTemplate.value = props.template
    })

    return {
      selectedFromValue,
      localTemplate,
      title,
      from,
      email,
      replyTo,
      validate,
      selectedFromLabel,
      selectFrom,
      subject,
    }
  },
})
</script>

<style lang="scss" scoped></style>
